/**
 * ResumeJourneyBanner — encart de reprise de parcours.
 *
 * Si un funnel a été commencé mais non finalisé (cf. useInProgressJourney),
 * propose de le reprendre. Levier de conversion fort (récupération d'abandons).
 *
 * - Mobile : barre en haut (n'entre pas en conflit avec le sticky CTA en bas).
 * - Desktop : encart discret en bas à gauche.
 * - Fermable (mémorisé en sessionStorage → pas de réaffichage dans la session).
 * - Tracking `funnel_resume` au clic.
 */

import { useState } from 'react';
import { ArrowRight, X, RotateCcw } from 'lucide-react';
import { useInProgressJourney } from '../../hooks/useInProgressJourney';
import { trackFunnelResume } from '../../lib/tracking';
import { captureEvent } from '../../lib/analytics';
import { Button } from '../ui/button';

const DISMISS_KEY = 'sef_resume_dismissed';
export const RESUME_DISMISS_EVENT = 'sef:resume-dismissed';

export function isResumeBannerDismissed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

export function ResumeJourneyBanner() {
  const journey = useInProgressJourney();
  const [dismissed, setDismissed] = useState<boolean>(() => isResumeBannerDismissed());

  if (!journey || dismissed) return null;

  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* ignore */
    }
    setDismissed(true);
    window.dispatchEvent(new Event(RESUME_DISMISS_EVENT));
  };

  const handleResume = () => {
    trackFunnelResume({ funnelId: journey.funnel }, journey.hint ?? 'resume', 0, {
      via: 'resume_banner',
    });
    captureEvent('resume_banner_click', { funnel: journey.funnel });
  };

  // Mobile : sous le header sticky (h-14 = 3.5rem) pour ne pas masquer logo/menu.
  return (
    <div className="fixed inset-x-3 top-[4.25rem] z-30 sm:inset-x-auto sm:bottom-5 sm:left-5 sm:top-auto sm:z-50 sm:max-w-sm">
      <div className="flex items-center gap-3 rounded-2xl border border-brand-200 bg-white/95 p-3 shadow-[0_8px_32px_rgba(10,10,10,.14)] backdrop-blur-md">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-100 text-brand-700">
          <RotateCcw className="h-5 w-5" strokeWidth={2} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-semibold text-ink">{journey.label}</p>
          {journey.hint && (
            <p className="truncate text-[11px] text-stone-500">{journey.hint}, reprise en 1 clic</p>
          )}
        </div>
        <Button asChild variant="brand" size="lg" className="h-10 shrink-0 px-3 text-[13px]">
          <a href={journey.href} onClick={handleResume}>
            Reprendre
            <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.2} />
          </a>
        </Button>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Masquer"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
